import { Box, Typography, Grid } from '@mui/material';
import Divider from '@mui/material/Divider';
import { useTranslation } from 'react-i18next';
// @ts-ignore
import { useEnums } from 'components/auth/PermissionApiEndpoint';
// @ts-ignore
import { enumFormatter } from 'lib/EnumFormatter';
import { UcWidget } from './UcWidget';

export interface UcSummaryTableProps {
    uom: string;
    rateCategories: RateDetails[];
    allowedRateChargeTypes?: string[];
    titleOverride?: string;
}

export interface RateDetails {
    cepOrgId: number;
    createdTs: string;
    description: string;
    exemptForHolidays: boolean;
    htmlColor: string;
    id: number;
    isDefault: boolean;
    meterStartTimestamp: string;
    name: string;
    rate: number;
    rateChargeId: number;
    rateChargeType: {
        value: string;
    };
    rateClassId: number;
    rateStartTimestamp: string;
    rateVersionStartDate: string;
    ruleDescriptions: string[];
    showOnUsageChart: boolean;
    updatedTs: string;
}

export const UcSummaryTable = (props: UcSummaryTableProps) => {
    const { t } = useTranslation();
    const { allowedRateChargeTypes, rateCategories, uom, titleOverride } = props;
    const rateChargeTypeEnums = useEnums("RateChargeType");

    const isRateAllowed = (rate: RateDetails) => {
        let result = (
            allowedRateChargeTypes === undefined
            || allowedRateChargeTypes?.length === 0
            || allowedRateChargeTypes?.includes(rate?.rateChargeType?.value)
        );

        return result;
    }

    const filteredRateCategories = (rateCategories?.filter((rate: RateDetails) => isRateAllowed(rate)));
    const groupedItems = filteredRateCategories?.reduce((result: any, item: any) => {
        const key = item.serialNo + (item.meterNickname ? ` - ${item.meterNickname}` : '');
        if (!result[key]) {
            result[key] = [];
        }
        result[key].push(item);
        return result;
    }, {});

    let filteredGroupedItems = null;
    if (groupedItems) {
        // after being grouped by serialNo we don't want duplicate rates in each
        filteredGroupedItems = Object.entries(groupedItems).reduce((acc: any, [key, items]: [string, any]) => {
            const seenIds = new Set<number>();
            acc[key] = items.filter((item: any) => {
                if (!seenIds.has(item.id && item.rateChargeId)) {
                    seenIds.add(item.id);
                    return true;
                }
                return false;
            });
            return acc;
        }, {} as any);
    }

    return (
        <div className="ucSummaryTable" data-cy-id={"ucSummaryTable"}>
            {filteredGroupedItems && Object.keys(filteredGroupedItems).length > 0 ?
                Object.keys(filteredGroupedItems)?.map((key: string, index: number) => (
                    <div key={`${key}_ rates`} style={{
                        marginBottom: index < (Object.keys(filteredGroupedItems).length - 1) ? 8 : 0
                    }}>
                        <UcWidget
                            key={`${key}_ rates`}
                            title={titleOverride ?? t('Meter') + " #" + key}
                            titleFixedFontSize={18}
                            alignTitleLeft={true}
                            contentPadding={'1rem'}
                            minHeight={"100px"}
                        >
                            {filteredGroupedItems[key].map((item: any, index: number) => (
                                <>
                                    {index > 0 && (
                                        <Divider sx={{ my: 1 }} />
                                    )}
                                    <Grid container spacing={1} justifyContent={'flex-start'} alignItems={'center'}>
                                        <Grid item>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    width: "20px",
                                                    height: "20px",
                                                    backgroundColor: `${item?.htmlColor}`,
                                                    borderRadius: "50%"
                                                }}>
                                            </div>
                                        </Grid>
                                        <Grid item xs={8} md={4}>
                                            <Typography fontWeight={'bold'} variant='body2'>
                                                {
                                                    (item?.rateChargeType?.value !== undefined)
                                                        ? enumFormatter(rateChargeTypeEnums, item?.rateChargeType?.value)
                                                        : enumFormatter(rateChargeTypeEnums, item?.rateChargeType)
                                                } - {item?.name}
                                            </Typography>
                                            <Typography variant='body2'>
                                                ${item?.rate}
                                                {(item?.rateChargeType?.value === "MONTHLY" || item?.rateChargeType === "MONTHLY") ? " per month" : "/" + uom}
                                            </Typography>
                                        </Grid>
                                        <Grid item>
                                            <Box sx={{ ml: 1 }}>
                                                {item?.ruleDescriptions?.length > 0 ? (
                                                    item?.ruleDescriptions?.map((item: string) => (
                                                        <Typography key={`${item}-description`} variant='body2'>
                                                            {'\u2022 ' + item}
                                                        </Typography>
                                                    ))
                                                ) : (
                                                    <Typography variant='body2'>- - -</Typography>
                                                )}   
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </>
                            ))}
                        </UcWidget>
                    </div>
                )) :
                <Typography sx={{ padding: "15px 25px 10px 25px" }}>
                    {t('No rates are currently available.')}
                </Typography>
            }
        </div>

    );
};